import { BufferGeometry, LineBasicMaterial, LineSegments, Mesh, Plane, Vector3 } from 'three';
import { watchImmediate } from '@vueuse/core';
import { stop, stopAll, type StopHandle } from '@/util';
import type { OverlaySceneContext } from '@/views/dicoms/overlay/scene/overlaySceneContext';
import type { OverlayStore } from '@/views/dicoms/overlay/overlayStore';
import { updatePlaneMeshIntersectionSegments } from '@/lib/threeScene/planeMeshIntersection';
import { computed, reactive } from 'vue';
import { updateOverlayMesh } from '@/views/dicoms/overlay/scene/overlayMesh';
import { getPlyModel } from '@/api/getPlyModel';
import type { CatstackPlaneId } from '@/domain/anatomy/catstackPlane';
import { intersectionColour } from "@/views/dicoms/CatStackImageUtil";

/** Update catstack cross-section outlines for a specific view type */
export function updateCatstackCrossSectionOutlines(
    context: OverlaySceneContext,
    store: OverlayStore,
    planeId: CatstackPlaneId,
): StopHandle {
    // Get the nodes for this specific view
    const nodes = store.getNodesForView(planeId);
    const femurMeshUpdate = updateOverlayMesh(context, nodes.operativeFemur, getPlyModel);

    // Make the femur mesh invisible since we only want the cross-section
    nodes.operativeFemur.visible = false;

    let clippingUpdate: StopHandle | null = null;

    return stopAll(
        femurMeshUpdate,
        watchImmediate(
            () => ({
                sliceIndex: store.catstacks[planeId].sliceIndex,
                catstacksData: store.catstacksData,
                geometry: nodes.operativeFemur.geometry,
            }),
            ({ sliceIndex, catstacksData, geometry }) => {
                stop(clippingUpdate);
                clippingUpdate = null;

                if (sliceIndex !== undefined && catstacksData && geometry) {
                    const catstackData = catstacksData[planeId];
                    clippingUpdate = _updateCatstackClippingPlane(
                        context,
                        store,
                        nodes,
                        sliceIndex,
                        catstackData,
                        planeId,
                    );
                }
            },
        ),
        () => stop(clippingUpdate),
    );
}

function _updateCatstackClippingPlane(
    context: OverlaySceneContext,
    store: OverlayStore,
    nodes: ReturnType<typeof store.getNodesForView>,
    sliceIndex: number,
    catstackData: { axis: { origin: Vector3; direction: Vector3; length: number } },
    planeId: CatstackPlaneId,
): StopHandle {
    const clippingPlane = reactive<Plane>(new Plane());

    return stopAll(
        watchImmediate(
            () => sliceIndex,
            (index) => {
                const { origin, direction, length } = catstackData.axis;
                const maxSliceIndex = store.catstacks[planeId].max || 100;
                const normalizedPosition = index / maxSliceIndex;
                const slicePosition = origin.clone().add(
                    direction.clone().multiplyScalar(normalizedPosition * length),
                );

                clippingPlane.setFromNormalAndCoplanarPoint(direction, slicePosition);
            },
        ),

        _updateFemurOutline(context, clippingPlane, nodes),
    );
}

function _updateFemurOutline(
    context: OverlaySceneContext,
    clippingPlane: Plane,
    nodes: ReturnType<OverlayStore['getNodesForView']>,
): StopHandle {
    const mesh = context.getObject(nodes.operativeFemur.id) as Mesh;
    if (!mesh || !mesh.isMesh) {
        return () => {
        };
    }

    const outlineMaterial = new LineBasicMaterial({
        color: intersectionColour,
        linewidth: 2,
        transparent: true,
        opacity: 0.9,
        depthTest: false,
    });

    const outlineSegments = new LineSegments(new BufferGeometry(), outlineMaterial);
    context.scene.add(outlineSegments);

    return stopAll(
        updatePlaneMeshIntersectionSegments(
            outlineSegments,
            clippingPlane,
            computed(() => nodes.operativeFemur.geometry),
            computed(() => context.worldTransform(nodes.operativeFemur.id)),
        ),
        () => {
            context.scene.remove(outlineSegments);
            outlineMaterial.dispose();
            outlineSegments.geometry.dispose();
        },
    );
}
